import { Collapse, Switch, Table, TableColumnsType, Checkbox, Tooltip } from 'antd';
import React from 'react';
import { GenieMemoryItem, GenieMemoryConfig } from '../../api/pushGenie';
import {
    GenieTableType,
    INJECTIONS_MEMORY_NAMES,
    LAB_TESTS_MEMORY_NAMES,
    MEDICATIONS_MEMORY_NAMES,
} from '../../constants';
import { infoIconNew } from '../../components/common/Icons';

const { Panel } = Collapse;

interface GenieMemoryConfigTable {
    config: GenieMemoryItem[];
    type: GenieTableType;
    updateConfig: React.Dispatch<React.SetStateAction<GenieMemoryConfig | undefined>>;
    isEnabled: boolean;
    loading: boolean;
}

const getTitle = (type: GenieTableType) => {
    switch (type) {
        case GenieTableType.MED:
            return 'Medications';
        case GenieTableType.INJ:
            return 'Injections';
        case GenieTableType.LAB:
            return 'Lab investigations';
        default:
            return '';
    }
};

const NAMES_BY_ID = (type: GenieTableType) => {
    if (type === GenieTableType.MED) return MEDICATIONS_MEMORY_NAMES;
    else if (type === GenieTableType.INJ) return INJECTIONS_MEMORY_NAMES;
    else return LAB_TESTS_MEMORY_NAMES;
};

const GenieMemoryConfigTable: React.FC<GenieMemoryConfigTable> = ({
    config,
    type,
    updateConfig,
    isEnabled,
    loading,
}) => {
    const columns: TableColumnsType<any> = [
        {
            title: 'Field',
            dataIndex: 'id',
            key: 'id',
            fixed: 'left',
            render: (id: string) => {
                return <p>{(NAMES_BY_ID(type) as Record<string, string>)[id]}</p>;
            },
        },
        {
            title: 'Remember',
            dataIndex: 'remember',
            width: '30%',
            key: 'remember',
            render: (remember: boolean, field: GenieMemoryItem) => {
                return (
                    <Checkbox
                        className="flex items-center justify-center cursor-pointer"
                        onChange={() => {
                            updateConfig((prev) => {
                                if (!prev) return prev;
                                const { medications = [], injections = [], labTests = [] } = prev;
                                if (type === GenieTableType.MED) {
                                    return {
                                        ...prev,
                                        medications: medications.map((item) =>
                                            item.id === field.id
                                                ? { ...item, remember: !item.remember }
                                                : item,
                                        ),
                                    };
                                } else if (type === GenieTableType.INJ) {
                                    return {
                                        ...prev,
                                        injections: injections.map((item) =>
                                            item.id === field.id
                                                ? { ...item, remember: !item.remember }
                                                : item,
                                        ),
                                    };
                                } else {
                                    return {
                                        ...prev,
                                        labTests: labTests.map((item) =>
                                            item.id === field.id
                                                ? { ...item, remember: !item.remember }
                                                : item,
                                        ),
                                    };
                                }
                            });
                        }}
                        checked={remember}
                    />
                );
            },
        },
    ];

    return (
        <div className="p-16 flex flex-col flex-1 h-full overflow-auto">
            {!loading ? (
                <Collapse activeKey={isEnabled ? ['1'] : []} bordered={false} className="rounded-8">
                    <Panel
                        key="1"
                        header={
                            <div
                                style={{ paddingRight: 'calc(15% - 22px)' }}
                                className="flex flex-row rounded-8 w-full justify-between items-center pl-8 py-8"
                            >
                                <div className="flex flex-row gap-8 items-center">
                                    <p className="text-darwin-neutral-1000 heading-3 bold">
                                        {getTitle(type)}
                                    </p>
                                    <Tooltip title="Turn off Genie suggestions while keeping search active.">
                                        {infoIconNew(
                                            'fill-darwin-neutral_new-1000 w-14 h-14 cursor-pointer',
                                        )}
                                    </Tooltip>
                                </div>
                                <Switch
                                    checked={isEnabled}
                                    onClick={() => {
                                        updateConfig((prev) => {
                                            if (type === GenieTableType.MED) {
                                                const newValue = !Boolean(
                                                    prev?.isMedicationsEnabled,
                                                );
                                                return {
                                                    ...prev,
                                                    isMedicationsEnabled: newValue,
                                                    medications: (
                                                                  prev?.medications || []
                                                              ).map((med) => ({
                                                                  ...med,
                                                                  remember: newValue,
                                                              })),
                                                };
                                            } else if (type === GenieTableType.INJ) {
                                                const newValue = !Boolean(
                                                    prev?.isInjectionsEnabled,
                                                );
                                                return {
                                                    ...prev,
                                                    isInjectionsEnabled: newValue,
                                                    injections: (prev?.injections || []).map(
                                                        (inj) => ({
                                                            ...inj,
                                                            remember: newValue,
                                                        }),
                                                    ),
                                                };
                                            } else {
                                                const newValue = !Boolean(prev?.isLabTestEnabled);
                                                return {
                                                    ...prev,
                                                    isLabTestEnabled: newValue,
                                                    labTests: (prev?.labTests || []).map(
                                                        (test) => ({
                                                            ...test,
                                                            remember: newValue,
                                                        }),
                                                    ),
                                                };
                                            }
                                        });
                                    }}
                                />
                            </div>
                        }
                        showArrow={false}
                    >
                        <div className="border-black">
                            <Table
                                columns={columns}
                                dataSource={config || []}
                                showHeader={false}
                                pagination={false}
                                bordered
                            />
                        </div>
                    </Panel>
                </Collapse>
            ) : null}
        </div>
    );
};

export default GenieMemoryConfigTable;
